import { useForm } from 'react-hook-form';
import { RxCross2 } from 'react-icons/rx';
import Input from '@/components/Common/Input';
import Select from '@/components/Common/Select';
import Button from '@/components/Common/Button';
import FormLoadingSkelton from './FormLoadingSkelton';
import { useNavigate, useParams } from 'react-router-dom';
import { BusinessProfileResponse } from '@/apis/business/types';
import { useGetAgencyAgents, useGetBusinesses } from '@/apis/agency/hook';
import { IntraofficeNoteRequest } from '@/apis/agency/intraofficeNotes/types';
import { Box, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useCreateOrUpdateIntraofficeNote, useGetIntraofficeNote } from '@/apis/agency/intraofficeNotes/hook';

export default function AddEditNoteDrawer() {
  const { noteId } = useParams();
  const navigate = useNavigate();

  const { data: businesses, isLoading: loadingBusinesses } = useGetBusinesses({
    per_page: 9999
  });

  const { data: intraOfficeNote, isLoading: loadingNote } = useGetIntraofficeNote();

  const { mutateAsync: createOrUpdateNote, isPending } = useCreateOrUpdateIntraofficeNote(true);

  const { control, handleSubmit, watch, setValue } = useForm<IntraofficeNoteRequest>({
    values: { intraoffice_note: intraOfficeNote ?? {} }
  });

  const noteValue = watch('intraoffice_note.note') ?? '';
  const selectedBusinessId = watch('intraoffice_note.business_id');

  const { data: businessAgents, isLoading: loadingBusinessAgents } = useGetAgencyAgents({
    business_id: selectedBusinessId!,
    per_page: 9999
  });

  const isInitialLoading = loadingBusinesses || loadingNote;

  const onChangeBusiness = ({ target: { value } }: any) => {
    setValue('intraoffice_note.business_id', value);
    setValue('intraoffice_note.agent_id', null);
  };

  const onSubmit = (payload: IntraofficeNoteRequest) => {
    createOrUpdateNote(payload);
  };

  return (
    <Drawer
      anchor='right'
      open
      onClose={() => navigate(-1)}
      PaperProps={{
        sx: { width: 520 }
      }}
    >
      <Box display='flex' alignItems='center' justifyContent='space-between' px={3} py={2}>
        <Typography fontWeight={500} fontSize={20}>
          {noteId ? 'Edit Intraoffice Note' : 'Create Intraoffice Note'}
        </Typography>

        <IconButton onClick={() => navigate(-1)}>
          <RxCross2 />
        </IconButton>
      </Box>

      <Divider />

      {isInitialLoading ? (
        <FormLoadingSkelton />
      ) : (
        <Box component='form' onSubmit={handleSubmit(onSubmit)} display='flex' flexDirection='column' height='100%'>
          <Box flex={1} overflow='auto' p={3}>
            <Stack spacing={3}>
              <Input name='intraoffice_note.title' label='Title' control={control} fullWidth required />

              <Box>
                <Input
                  name='intraoffice_note.note'
                  label='Note'
                  control={control}
                  multiline
                  minRows={4}
                  maxRows={7}
                  inputProps={{ maxLength: 600 }}
                  fullWidth
                  required
                />

                <Typography variant='caption' color='text.secondary' display='flex' justifyContent='flex-end' mt={0.5}>
                  {noteValue.length}/600 Characters
                </Typography>
              </Box>

              <Select
                name='intraoffice_note.business_id'
                label='Business'
                disabled={loadingBusinesses}
                control={control}
                onChange={onChangeBusiness}
                searchable
                options={
                  businesses?.data.map((business: BusinessProfileResponse) => ({
                    value: business.id,
                    label: business.name
                  })) ?? []
                }
              />

              <Select
                label='Agent'
                name='intraoffice_note.agent_id'
                disabled={loadingBusinessAgents}
                control={control}
                options={
                  businessAgents?.data.map((agent: any) => ({
                    value: agent.id,
                    label: agent.name
                  })) ?? []
                }
                fullWidth
              />
            </Stack>
          </Box>

          <Divider />

          <Box p={2} display='flex' justifyContent='flex-end' gap={2}>
            <Button
              variant='outlined'
              sx={{ minHeight: '38px', borderColor: '#ECECEC', color: '#777777' }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>

            <Button variant='contained' type='submit' sx={{ minHeight: '38px' }} isPending={isPending}>
              {noteId ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
